import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { BrandLockup } from '~/components/BrandLockup'
import { useAuth } from '~/auth'
import styles from './auth.module.css'

type AuthMode = 'phone' | 'email'

// Map auth-server error codes to human-friendly copy.
function mapLoginError(error: string): string {
  switch (error) {
    case 'invalid_credentials':
      return 'Incorrect phone/email or password'
    case 'email_verification_required':
      return 'Please verify your email first'
    default:
      return 'Unable to sign in. Please try again.'
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const { reset } = useSearch({ from: '/login' })

  const [authMode, setAuthMode] = useState<AuthMode>('email')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')
    setIsSubmitting(true)
    try {
      if (authMode === 'phone') {
        setError('Phone OTP sign-in needs WorkOS phone identity mapping. Use email sign-in for now.')
        return
      }
      const result = await signIn(email.trim(), password)
      if (result.ok) {
        await navigate({ to: '/' })
        return
      }
      setError(mapLoginError(result.error))
    } catch {
      setError('Unable to sign in. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const activeIdentifier = authMode === 'phone' ? phone : email
  const looksLikeEmailOnPhone = authMode === 'phone' && activeIdentifier.includes('@')

  return (
    <div className={styles.page}>
      <span className={styles.bgNumber}>01</span>

      <div className={styles.container}>
        <header className={styles.header}>
          <BrandLockup variant="auth" meta="DELIVERING WITH CARE" />
        </header>

        <main className={styles.main}>
          <Heading level="h2" size="xl" className={styles.formTitle}>
            LOGIN
          </Heading>

          {reset === 'success' && (
            <div className={styles.success}>
              Password reset. Please sign in with your new password.
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.modeSwitch} aria-label="Choose login identifier">
              <button
                className={`${styles.modeButton} ${authMode === 'phone' ? styles.modeButtonActive : ''}`}
                type="button"
                onClick={() => setAuthMode('phone')}
              >
                PHONE
              </button>
              <button
                className={`${styles.modeButton} ${authMode === 'email' ? styles.modeButtonActive : ''}`}
                type="button"
                onClick={() => setAuthMode('email')}
              >
                EMAIL
              </button>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="identifier">
                {authMode === 'phone' ? 'PHONE NUMBER' : 'EMAIL'}
              </label>
              {authMode === 'phone' ? (
                <>
                  <div className={styles.identifierRow}>
                    <span className={styles.countryPrefix}>ID +62</span>
                    <input
                      className={`${styles.input} ${styles.inlineInput}`}
                      id="identifier"
                      name="identifier"
                      type="tel"
                      inputMode="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                      autoFocus
                      required
                    />
                  </div>
                  <span className={styles.helperText}>
                    Phone OTP is used for verification. WorkOS sign-in currently requires email.
                  </span>
                  {looksLikeEmailOnPhone && (
                    <button
                      className={styles.switchHint}
                      type="button"
                      onClick={() => {
                        setEmail(phone)
                        setPhone('')
                        setAuthMode('email')
                      }}
                    >
                      Looks like an email — switch?
                    </button>
                  )}
                </>
              ) : (
                <input
                  className={styles.input}
                  id="identifier"
                  name="identifier"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">
                PASSWORD
              </label>
              <input
                className={styles.input}
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <div className={styles.options}>
              <Link className={styles.forgotLink} to="/forgot-password">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" variant="accent" size="lg" width="full" disabled={isSubmitting}>
              {isSubmitting ? 'SIGNING IN...' : 'SIGN IN'}
            </Button>
          </form>

          <div className={styles.footer}>
            <Text variant="muted" size="sm">
              DON&apos;T HAVE AN ACCOUNT?
            </Text>
            <Link className={styles.footerLink} to="/register">
              REGISTER
            </Link>
          </div>
        </main>
      </div>
    </div>
  )
}
